import React from 'react';
import { makeContextCacher } from 'js/sign-components/common/legacy-context-utilities';
import { useAppContext } from 'hello-react/web-app-client';
import PropTypes from 'prop-types';
import LoaderSpinner from 'js/sign-components/loader-spinner';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { getCSRFToken } from 'js/sign-components/common/hs-fetch';
import signatureContext from 'hellospa/components/signature-modal-context/context';
import { Type } from 'hellospa/components/signature-modal-context/prop-types';
import CONSTANTS from 'js/sign-components/signature-modal/constants';

/**
 * <SignatureProvider holds the data and handles some of the logic for how the
 * signature modal works. It was created when I was building the Editor because
 * the signature moal required a lot of data that I didn't want to drill down
 * all the way to the signature field on the page.
 *
 * I think this could be done a lot better if we try replacing the signature
 * modal from top to bottom. Instead of this split context thing, the new
 * implementation can just fetch its own data.
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535230557410&cot=14
 */
class SignatureProvider extends React.PureComponent {
  static propTypes = {
    isMobile: PropTypes.bool,
    signature: PropTypes.shape({
      type: Type,
    }),
    primarySignatureGuid: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    appContext: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    defaultSignatureType: PropTypes.string,
    allowedSignatureTypes: PropTypes.object,
    hideSpinner: PropTypes.bool,
    isEmbedded: PropTypes.bool,
    isDropbox: PropTypes.bool,
    canInsertEverywhere: PropTypes.bool,
    PaginateSignatures: PropTypes.bool,
    allowColorSig: PropTypes.bool,
  };

  retries = 0;

  contextCache = makeContextCacher();

  /** Convert signature data list to signatures list */
  fromSignaturesData(data) {
    const signatures = [];
    if (data) {
      return data.map((item) => ({
        ...item,
        type: item.type_code,
        createType: item.create_type_code,
        guid: item.signature_id,
        createdAt: item.created_at ? new Date(item.created_at * 1000) : null,
        lastUsedAt: item.last_used_at ? new Date(item.last_used_at * 1000) : null,
        width: item.width,
        height: item.height,
        signature: item.signature || null,
      }));
    }
    return signatures;
  }

  getInitialValue(userSettings, type = 'signature') {
    let initialValue = '';
    if (userSettings.firstName) {
      initialValue += type === 'signature' ? userSettings.firstName : userSettings.firstName[0];
    }

    if (userSettings.lastName) {
      initialValue += type === 'signature' ? ` ${userSettings.lastName}` : userSettings.lastName[0];
    }

    return initialValue;
  }

  async componentDidMount() {
    let initiallySelectedSignature;
    let signatures = [];
    const { primarySignatureGuid, firstName, lastName } = this.props;

    if (this.props.PaginateSignatures) {
      initiallySelectedSignature = await this.props.appContext.signatures
        .fetchSignatureByGuid(primarySignatureGuid);
      if (!initiallySelectedSignature) {
        const page = 1;
        const limit = 1;
        // grab the first signature if a default signature is not specified
        signatures = await this.getPaginatedSignatures(CONSTANTS.TYPE_SIGNATURE, page, limit);
      }
    } else {
      signatures = await this.getPaginatedSignatures(CONSTANTS.TYPE_SIGNATURE);
      this.getPaginatedSignatures(CONSTANTS.TYPE_INITIALS);
      const selected = primarySignatureGuid;
      initiallySelectedSignature = signatures.find((item) => item.guid === selected);
    }

    // allowed signature types are passed in as an object with key as the type and a boolean value,
    const allowedSignatureTypes = this.props.allowedSignatureTypes != null
      ? Object.keys(this.props.allowedSignatureTypes).filter((t) => {
        return this.props.allowedSignatureTypes[t] === true;
      }) : CONSTANTS.TABS;

    let defaultTab = CONSTANTS.TAB_SAVED;
    // if it's embedded, don't default to the Saved Signatures tab
    if (this.props.isEmbedded && !this.props.isDropbox) {
      // check if the default tab is part of the allowed signature types
      if (allowedSignatureTypes.includes(CONSTANTS.TAB_DRAW)) {
        defaultTab = CONSTANTS.TAB_DRAW;
      } else {
        // get the first type that's not the saved signatures tab
        defaultTab = allowedSignatureTypes.find((type) => type !== CONSTANTS.TAB_SAVED);
      }
    }

    // check if the default type is part of allowed types
    const defaultSignatureType = allowedSignatureTypes.includes(this.props.defaultSignatureType)
      ? this.props.defaultSignatureType
      : defaultTab;

    this.setState({
      // Set initial data
      savedSignatures: signatures,
      selectedSavedSignature: initiallySelectedSignature || signatures[0],
      // Default states
      initialsInitialTypeInValue: this.getInitialValue({ firstName, lastName }, 'initial'),
      signatureInitialTypeInValue: this.getInitialValue({ firstName, lastName }),
      isCreatingDrawnSignature: false,
      isCreatingTypedSignature: false,
      isUploadingSignature: false,
      uploadedSignature: null,
      selectedTab: defaultSignatureType,
      enabledTabs: allowedSignatureTypes,
      isEmbedded: this.props.isEmbedded,
      isDropbox: this.props.isDropbox,
      canInsertEverywhere: this.props.canInsertEverywhere,
    });
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.allowedSignatureTypes !== this.props.allowedSignatureTypes
      || prevProps.defaultSignatureType !== this.props.defaultSignatureType) {
      const allowedSignatureTypes = this.props.allowedSignatureTypes != null
        ? Object.keys(this.props.allowedSignatureTypes).filter((t) => {
          return this.props.allowedSignatureTypes[t] === true;
        }) : CONSTANTS.TABS;

      let defaultTab = CONSTANTS.TAB_SAVED;
      // if it's embedded, don't default to the Saved Signatures tab
      if (this.props.isEmbedded && !this.props.isDropbox) {
        // check if the default tab is part of the allowed signature types
        if (allowedSignatureTypes.includes(CONSTANTS.TAB_DRAW)) {
          defaultTab = CONSTANTS.TAB_DRAW;
        } else {
          // get the first type that's not the saved signatures tab
          defaultTab = allowedSignatureTypes.find((type) => type !== CONSTANTS.TAB_SAVED);
        }
      }

      // check if the default type is part of allowed types
      const defaultSignatureType = allowedSignatureTypes.includes(this.props.defaultSignatureType)
        ? this.props.defaultSignatureType
        : defaultTab;

      this.setState({
        enabledTabs: allowedSignatureTypes,
        selectedTab: defaultSignatureType,
      });
    }
  }


  handleSelectTab = (selectedTab) => this.setState({ selectedTab });

  getSignatureUrl = (signature, threshold, degrees) => {
    let thresholdAndDegrees = '';
    if (threshold !== undefined && degrees !== undefined) {
      thresholdAndDegrees = `&threshold=${threshold}&degrees=${degrees}`;
    }
    let makeColorSig = '';
    if (this.props.allowColorSig === true) {
      makeColorSig = '&allow_color_signature=true';
    }
    // Maybe this should be moved into attachmentURL?
    const withSession = HfReactHelper.attachSessionInfoToUrl(`/attachment/view?sig_guid=${signature.guid}${thresholdAndDegrees}${makeColorSig}`);

    return this.props.appContext.signatures.signatureURL(withSession, signature.guid);
  };

  onSavedSignatureRemove = async (signature) => {
    try {
      const success = await this.props.appContext.signatures.removeSignature(
        signature.guid,
      );
      if (success) {
        if (signature.type === CONSTANTS.TYPE_SIGNATURE) {
          this.setState({
            savedSignatures: this.state.savedSignatures.filter((s) => s !== signature),
          });
        } else {
          this.setState({
            savedInitials: this.state.savedInitials.filter((s) => s !== signature),
          });
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  onSavedSignatureSelect = (selectedSavedSignature) => {
    this.setState({
      selectedSavedSignature,
    });
  };

  onRotateSignature = (signature) => {
    return this.props.appContext.signatures.rotateSignature(
      signature.guid,
      CONSTANTS.ROTATE_DEGREES_AMOUNT,
    );
  };

  onUploadSignature = async (file, type) => {
    this.setState({ isUploadingSignature: true });

    const sig = {
      file,
      type,
      allowColorSig: this.props.allowColorSig,
      createType: CONSTANTS.SIGNATURE_TYPE_UPLOAD,
    };

    const response = await this.props.appContext.signatures.uploadSignatureFile(sig);
    // Setup the signature obj from the response
    if (response) {
      sig.guid = response.signature_id;
      sig.width = response.width;
      sig.height = response.height;

      this.setState({
        uploadedSignature: sig,
        isUploadingSignature: false,
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('Could not upload the signature file');
      this.setState({ isUploadingSignature: false });
    }
  };

  onSignatureData = (data) => {
    const uploadFunc = data.create_type_code === CONSTANTS.SIGNATURE_TYPE_CANVAS
      ? this.props.appContext.signatures.uploadCanvas
      : this.props.appContext.signatures.upload;
    return uploadFunc(data)
      .then((obj) => {
        const signatures = this.fromSignaturesData([obj]);
        if (obj && typeof obj.signature_id !== 'undefined') {
          // Replace signature_id with signature_guid,
          // because that's how we expect it in the code later
          obj.signature_guid = obj.signature_id;
          delete obj.signature_id;
        }
        // At this point, an uploaded signature needs a guid
        // and we can't recover from this
        if (obj && !obj.signature_guid) {
          throw new Error("Uploaded signature but could not determine it's guid");
        }
        if (obj.type_code === CONSTANTS.TYPE_SIGNATURE) {
          this.setState({
            savedSignatures: signatures,
          });
        } else {
          this.setState({
            savedInitials: signatures,
          });
        }
        return obj;
      });
  };

  clearUploadedSignature = () => {
    this.setState({
      uploadedSignature: null,
    });
  };

  getPaginatedSignatures = async (type, newPage = 1, overrideLimit) => {
    let page;
    let limit;
    if (this.props.PaginateSignatures) {
      page = newPage;
      if (overrideLimit) {
        limit = overrideLimit;
      } else {
        limit = type === CONSTANTS.TYPE_SIGNATURE
          ? CONSTANTS.SIGNATURES_PER_PAGE
          : CONSTANTS.INITIALS_PER_PAGE;
      }
    }
    const { list, count } = await this.props.appContext.signatures.fetchSignatures(
      type,
      page,
      limit,
    );
    const data = this.fromSignaturesData(list);

    if (type === CONSTANTS.TYPE_INITIALS) {
      this.setState({
        savedInitials: data,
        initialsCount: count,
      });
    } else {
      this.setState({
        savedSignatures: data,
        signaturesCount: count,
      });
    }
    return data;
  };

  render() {
    if (this.state == null && !this.props.hideSpinner) {
      return (
        <LoaderSpinner />
      );
    }

    const {
      signaturesCount,
      initialsCount,
      savedSignatures,
      savedInitials,
      selectedSavedSignature,
      isUploadingSignature,
      uploadedSignature,
      initialsInitialTypeInValue,
      signatureInitialTypeInValue,
      selectedTab,
      enabledTabs,
      isEmbedded,
      isDropbox,
      canInsertEverywhere,
    } = this.state || {};

    const value = this.contextCache({
      getSignatureUrl: this.getSignatureUrl,
      onSavedSignatureRemove: this.onSavedSignatureRemove,
      onSavedSignatureSelect: this.onSavedSignatureSelect,
      onRotateSignature: this.onRotateSignature,
      onUploadSignature: this.onUploadSignature,
      onSignatureData: this.onSignatureData,
      onTabSelect: this.handleSelectTab,
      clearUploadedSignature: this.clearUploadedSignature,
      getPaginatedSignatures: this.getPaginatedSignatures,
      getPaginatedInitials: this.getPaginatedInitials,
      signaturesCount,
      initialsCount,
      savedSignatures,
      savedInitials,
      selectedSavedSignature,
      csrfToken: getCSRFToken(),
      isMobile: this.props.isMobile || false,
      isUploadingSignature,
      uploadedSignature,
      initialsInitialTypeInValue,
      signatureInitialTypeInValue,
      selectedTab,
      enabledTabs,
      isEmbedded,
      isDropbox,
      canInsertEverywhere,
    });

    return (
      <signatureContext.Provider value={value}>
        {this.props.children}
      </signatureContext.Provider>
    );
  }
}


export default function SignatureProviderImplWrapper(props) {
  const appContext = useAppContext();

  const featureflagContext = useFeatureFlag('HelloSign_Rollout_Paginate_Signatures');
  return <SignatureProvider
    {...props}
    appContext={appContext}
    PaginateSignatures={featureflagContext} />;
}
